import type {
  CampaignCreationCapabilities,
  SmartCreationNegativeCandidate,
  SmartCreationNegativeCandidates,
  SmartCreationReviewAdGroup,
} from "@adpilot/shared";
import {
  IconAlertTriangle,
  IconInfoCircle,
  IconRefresh,
  IconTargetOff,
  IconX,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { fetchSmartCreationNegativeCandidates } from "@/lib/smart-creation-api";
import { ReviewQuickAddTargets } from "./ReviewQuickAddTargets";
import { type QuickTargetValue, quickTargetIdentity } from "./review-quick-targets";

export interface NegativeEditorEntry {
  id: string;
  label: string;
  match: string;
}
interface NegativeCandidateRow extends NegativeEditorEntry {
  candidate?: SmartCreationNegativeCandidate;
  origins: SmartCreationNegativeCandidate[];
}
const NEGATIVE_PAGE_SIZE = 20;
interface ReviewNegativeTargetEditorProps {
  shopId?: string;
  group: SmartCreationReviewAdGroup;
  targetAcos: number | null;
  currencyCode: string;
  capabilities?: CampaignCreationCapabilities;
  disabled: boolean;
  enabled: boolean;
  entries: NegativeEditorEntry[];
  retainedEntries: NegativeEditorEntry[];
  positiveEntries: NegativeEditorEntry[];
  onChange(entries: NegativeEditorEntry[]): void;
  onSelectCandidate(candidate: SmartCreationNegativeCandidate): void;
  onAdd(values: QuickTargetValue[]): { added: number; skipped: number; error?: string };
}

export function negativeCandidateLabel(candidate: SmartCreationNegativeCandidate): string {
  return candidate.type === "keyword" ? candidate.text : candidate.asin;
}
export function negativeCandidateMatch(candidate: SmartCreationNegativeCandidate): string {
  return candidate.type === "keyword" ? candidate.matchType.toUpperCase() : "product";
}
function identity(entry: NegativeEditorEntry): string {
  return quickTargetIdentity(entry.match, entry.label);
}
function normalize(text: string): string {
  return text.trim().replace(/\s+/g, " ").toLocaleLowerCase("en-US");
}
function resetViewport(region: HTMLElement | null): void {
  const viewport = region?.querySelector<HTMLElement>('[data-slot="data-table-viewport"]');
  if (viewport) viewport.scrollTop = 0;
}
function overlappingTargets(
  negative: NegativeEditorEntry,
  positives: NegativeEditorEntry[],
): string[] {
  const text = normalize(negative.label);
  return positives
    .filter((positive) => {
      if (negative.match === "product")
        return ["exact", "expanded"].includes(positive.match) && normalize(positive.label) === text;
      if (negative.match === "NEGATIVE_EXACT")
        return (
          ["exact", "phrase", "broad"].includes(positive.match) &&
          normalize(positive.label) === text
        );
      if (negative.match !== "NEGATIVE_PHRASE") return false;
      if (!["exact", "phrase", "broad"].includes(positive.match)) return false;
      return ` ${normalize(positive.label)} `.includes(` ${text} `);
    })
    .map((positive) => positive.label);
}

/** Candidate IDs preserve selection provenance without persisting client-supplied performance. */
export function ReviewNegativeTargetEditor({
  shopId,
  group,
  targetAcos,
  currencyCode,
  capabilities,
  disabled,
  enabled,
  entries,
  retainedEntries,
  positiveEntries,
  onChange,
  onSelectCandidate,
  onAdd,
}: ReviewNegativeTargetEditorProps) {
  const { t, i18n } = useTranslation("smartCreation");
  const [search, setSearch] = useState("");
  const [selectedSearch, setSelectedSearch] = useState("");
  const [matchFilter, setMatchFilter] = useState("all");
  const [selectedMatchFilter, setSelectedMatchFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [candidatePage, setCandidatePage] = useState(1);
  const [selectedPage, setSelectedPage] = useState(1);
  const candidateRegion = useRef<HTMLElement>(null);
  const selectedRegion = useRef<HTMLElement>(null);
  const products = useMemo(
    () =>
      group.products
        .map(({ sku, asin }) => ({ sku, asin }))
        .sort((a, b) => a.sku.localeCompare(b.sku)),
    [group.products],
  );
  const query = useQuery({
    queryKey: ["smart-creation-negative-candidates", shopId, products, targetAcos],
    queryFn: () => {
      if (!shopId) throw new Error("Negative candidates require a Shop");
      return fetchSmartCreationNegativeCandidates(shopId, {
        products,
        ...(targetAcos == null ? {} : { targetAcos }),
      });
    },
    enabled: enabled && Boolean(shopId && products.length),
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const data = query.data;
  const candidates = useMemo(
    () => [...(data?.history.candidates ?? []), ...(data?.recommendations.candidates ?? [])],
    [data],
  );
  const candidateById = useMemo(
    () => new Map(candidates.map((candidate) => [candidate.id, candidate])),
    [candidates],
  );
  // A copied, missing or edited target cannot inherit the provenance of a different expression.
  const sourceCandidate = useCallback(
    (entry: NegativeEditorEntry) => {
      const id = /^negative-candidate:([a-f0-9]{64}):/.exec(entry.id)?.[1];
      const candidate = id ? candidateById.get(id) : undefined;
      return candidate &&
        identity(entry) ===
          quickTargetIdentity(negativeCandidateMatch(candidate), negativeCandidateLabel(candidate))
        ? candidate
        : undefined;
    },
    [candidateById],
  );
  const source = useCallback(
    (entry: NegativeEditorEntry, candidate?: SmartCreationNegativeCandidate) =>
      candidate?.source ??
      sourceCandidate(entry)?.source ??
      (entry.id.startsWith("quick:") ? "user" : "unknown"),
    [sourceCandidate],
  );
  const sourceLabel = useCallback(
    (entry: NegativeEditorEntry, candidate?: SmartCreationNegativeCandidate) =>
      t(`review.negativeTransfer.sources.${source(entry, candidate)}`),
    [source, t],
  );
  const matchLabel = useCallback(
    (match: string) =>
      t(
        match === "product"
          ? "review.quickAdd.negativeProduct"
          : match === "NEGATIVE_PHRASE"
            ? "review.match.negativePhrase"
            : "review.match.negativeExact",
      ),
    [t],
  );
  const currency = data?.currencyCode ?? currencyCode;
  const money = new Intl.NumberFormat(i18n.resolvedLanguage, {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  });
  const candidateRows = useMemo(() => {
    const rowsByIdentity = new Map<string, NegativeCandidateRow>();
    for (const candidate of candidates) {
      const entry = {
        id: candidate.id,
        label: negativeCandidateLabel(candidate),
        match: negativeCandidateMatch(candidate),
      };
      const key = `${identity(entry)}:${candidate.source}`;
      const existing = rowsByIdentity.get(key);
      if (existing) existing.origins.push(candidate);
      else rowsByIdentity.set(key, { ...entry, candidate, origins: [candidate] });
    }
    return [...rowsByIdentity.values()];
  }, [candidates]);
  const candidateIdentities = useMemo(() => new Set(candidateRows.map(identity)), [candidateRows]);
  const rows = useMemo(() => {
    const localRows = new Map<string, NegativeCandidateRow>();
    for (const entry of [...retainedEntries, ...entries]) {
      const key = identity(entry);
      if (!candidateIdentities.has(key) && !localRows.has(key))
        localRows.set(key, { ...entry, origins: [] });
    }
    return localRows.size ? [...candidateRows, ...localRows.values()] : candidateRows;
  }, [candidateRows, candidateIdentities, retainedEntries, entries]);
  const searchText = useCallback(
    (
      entry: NegativeEditorEntry,
      candidate?: SmartCreationNegativeCandidate,
      origins: SmartCreationNegativeCandidate[] = [],
    ) =>
      `${entry.label} ${matchLabel(entry.match)} ${sourceLabel(entry, candidate)} ${[candidate, ...origins].map((origin) => `${origin?.history?.campaignName ?? ""} ${origin?.history?.adGroupName ?? ""}`).join(" ")}`.toLocaleLowerCase(
        i18n.resolvedLanguage,
      ),
    [matchLabel, sourceLabel, i18n.resolvedLanguage],
  );
  const searchableCandidates = useMemo(
    () =>
      rows.map((entry) => ({
        entry,
        source: source(entry, entry.candidate),
        text: searchText(entry, entry.candidate, entry.origins),
      })),
    [rows, source, searchText],
  );
  const normalizedSearch = search.trim().toLocaleLowerCase(i18n.resolvedLanguage);
  const visibleCandidates = useMemo(
    () =>
      searchableCandidates
        .filter(
          ({ entry, source: candidateSource, text }) =>
            (matchFilter === "all" || matchFilter === entry.match) &&
            (sourceFilter === "all" || sourceFilter === candidateSource) &&
            text.includes(normalizedSearch),
        )
        .map(({ entry }) => entry),
    [searchableCandidates, matchFilter, sourceFilter, normalizedSearch],
  );
  const selectedIdentities = useMemo(() => new Set(entries.map(identity)), [entries]);
  const normalizedSelectedSearch = selectedSearch.trim().toLocaleLowerCase(i18n.resolvedLanguage);
  const visibleSelected = useMemo(
    () =>
      entries.filter(
        (entry) =>
          (selectedMatchFilter === "all" || entry.match === selectedMatchFilter) &&
          searchText(entry, sourceCandidate(entry)).includes(normalizedSelectedSearch),
      ),
    [entries, selectedMatchFilter, normalizedSelectedSearch, searchText, sourceCandidate],
  );
  const candidatePageCount = Math.max(1, Math.ceil(visibleCandidates.length / NEGATIVE_PAGE_SIZE));
  const selectedPageCount = Math.max(1, Math.ceil(visibleSelected.length / NEGATIVE_PAGE_SIZE));
  const currentCandidatePage = Math.min(candidatePage, candidatePageCount);
  const currentSelectedPage = Math.min(selectedPage, selectedPageCount);
  const pageCandidates = visibleCandidates.slice(
    (currentCandidatePage - 1) * NEGATIVE_PAGE_SIZE,
    currentCandidatePage * NEGATIVE_PAGE_SIZE,
  );
  const pageSelected = visibleSelected.slice(
    (currentSelectedPage - 1) * NEGATIVE_PAGE_SIZE,
    currentSelectedPage * NEGATIVE_PAGE_SIZE,
  );
  useEffect(() => {
    if (candidatePage > candidatePageCount) {
      setCandidatePage(candidatePageCount);
      resetViewport(candidateRegion.current);
    }
  }, [candidatePage, candidatePageCount]);
  useEffect(() => {
    if (selectedPage > selectedPageCount) {
      setSelectedPage(selectedPageCount);
      resetViewport(selectedRegion.current);
    }
  }, [selectedPage, selectedPageCount]);
  function changeCandidatePage(page: number) {
    setCandidatePage(page);
    resetViewport(candidateRegion.current);
  }
  function changeSelectedPage(page: number) {
    setSelectedPage(page);
    resetViewport(selectedRegion.current);
  }
  const allSelected =
    pageCandidates.length > 0 &&
    pageCandidates.every((entry) => selectedIdentities.has(identity(entry)));
  const partiallySelected =
    !allSelected && pageCandidates.some((entry) => selectedIdentities.has(identity(entry)));
  const overlaps = useMemo(
    () => entries.filter((entry) => overlappingTargets(entry, positiveEntries).length > 0),
    [entries, positiveEntries],
  );
  function select(entry: NegativeCandidateRow, checked: boolean) {
    if (disabled) return;
    if (!checked) onChange(entries.filter((item) => identity(item) !== identity(entry)));
    else if (!selectedIdentities.has(identity(entry))) {
      if (entry.candidate) onSelectCandidate(entry.candidate);
      else onChange([...entries, entry]);
    }
  }
  function selectVisible(checked: boolean) {
    if (disabled) return;
    if (!checked) {
      const visible = new Set(pageCandidates.map(identity));
      onChange(entries.filter((entry) => !visible.has(identity(entry))));
    } else {
      const additions = new Map<string, NegativeEditorEntry>();
      for (const row of pageCandidates) {
        if (selectedIdentities.has(identity(row)) || additions.has(identity(row))) continue;
        const id = row.candidate
          ? `negative-candidate:${row.candidate.id}:${crypto.randomUUID()}`
          : row.id;
        additions.set(identity(row), { id, label: row.label, match: row.match });
      }
      onChange([...entries, ...additions.values()]);
    }
  }
  const filter = (value: string, setValue: (next: string) => void, label: string) => (
    <Select value={value} onValueChange={(next) => setValue(next ?? "all")}>
      <SelectTrigger size="sm" className="h-8 w-28 shrink-0 text-xs" aria-label={label}>
        <SelectValue>
          {value === "all" ? t("review.targetSelection.allMatches") : matchLabel(value)}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t("review.targetSelection.allMatches")}</SelectItem>
        <SelectItem value="NEGATIVE_EXACT">{matchLabel("NEGATIVE_EXACT")}</SelectItem>
        <SelectItem value="NEGATIVE_PHRASE">{matchLabel("NEGATIVE_PHRASE")}</SelectItem>
        <SelectItem value="product">{matchLabel("product")}</SelectItem>
      </SelectContent>
    </Select>
  );
  function sourceCell(
    entry: NegativeEditorEntry,
    candidate?: SmartCreationNegativeCandidate,
    origins: SmartCreationNegativeCandidate[] = [],
  ) {
    const actual = candidate ?? sourceCandidate(entry);
    const history = actual?.history;
    return (
      <Tooltip>
        <TooltipTrigger
          render={<button type="button" className="w-full min-w-0 cursor-default text-left" />}
        >
          <span className="block truncate">{sourceLabel(entry, actual)}</span>
          {history ? (
            <span className="block truncate text-[10px] text-muted-foreground">
              {history.adGroupName ?? history.campaignName}
            </span>
          ) : null}
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="smart-creation-preview max-w-96">
          <div className="flex flex-col gap-1">
            {history ? (
              <>
                <span>
                  {t(`review.negativeTransfer.scopes.${history.scope}`)} · {history.campaignName}
                  {history.adGroupName ? ` / ${history.adGroupName}` : ""}
                </span>
                <span>
                  {t("review.negativeTransfer.campaignStatus")}:{" "}
                  {t(`review.negativeTransfer.status.${history.campaignStatus}`)}
                  {history.adGroupStatus
                    ? ` · ${t("review.negativeTransfer.adGroupStatus")}: ${t(`review.negativeTransfer.status.${history.adGroupStatus}`)}`
                    : ""}
                </span>
                <span>
                  {t("review.negativeTransfer.originalStatus")}:{" "}
                  {t(`review.negativeTransfer.status.${history.targetStatus}`)} ·{" "}
                  {t("review.negativeTransfer.synced", {
                    date: new Date(history.syncedAt).toLocaleString(i18n.resolvedLanguage),
                  })}
                </span>
                {origins.length > 1 ? (
                  <>
                    <span>
                      {t("review.negativeTransfer.moreOrigins", { count: origins.length })}
                    </span>
                    {origins.slice(1, 4).map((origin) => (
                      <span key={origin.id} className="truncate">
                        {origin.history?.campaignName}
                        {origin.history?.adGroupName ? ` / ${origin.history.adGroupName}` : ""}
                      </span>
                    ))}
                  </>
                ) : null}
              </>
            ) : actual?.evidence ? (
              <span>
                {t("review.negativeTransfer.evidenceSource", {
                  sku: actual.evidence.sku,
                  count: actual.evidence.adGroupIds.length,
                })}
              </span>
            ) : (
              <span>{sourceLabel(entry)}</span>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }
  function evidenceCell(
    candidate: SmartCreationNegativeCandidate,
    current: SmartCreationNegativeCandidates,
  ) {
    const evidence = candidate.evidence;
    if (!evidence)
      return (
        <span className="text-[10px] text-muted-foreground">
          {candidate.history
            ? candidate.history.campaignStatus !== "enabled"
              ? t("review.negativeTransfer.parentCampaignStatus", {
                  status: t(`review.negativeTransfer.status.${candidate.history.campaignStatus}`),
                })
              : candidate.history.adGroupStatus && candidate.history.adGroupStatus !== "enabled"
                ? t("review.negativeTransfer.parentAdGroupStatus", {
                    status: t(`review.negativeTransfer.status.${candidate.history.adGroupStatus}`),
                  })
                : t(`review.negativeTransfer.status.${candidate.history.targetStatus}`)
            : ""}
        </span>
      );
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              className="w-full cursor-default text-left text-[10px] leading-4 tabular-nums"
            />
          }
        >
          <span className="block truncate">
            {current.policy.startDate.slice(5)}–{current.policy.endDate.slice(5)}
          </span>
          <span className="block truncate text-muted-foreground">
            {evidence.clicks} · {money.format(evidence.spend)} · {evidence.orders}
          </span>
        </TooltipTrigger>
        <TooltipContent className="smart-creation-preview max-w-96">
          <div className="flex flex-col gap-1">
            <span>
              {current.policy.startDate}–{current.policy.endDate}
            </span>
            <span>
              {t("review.negativeTransfer.evidenceMetrics", {
                clicks: evidence.clicks,
                spend: money.format(evidence.spend),
                orders: evidence.orders,
              })}
            </span>
            <span>
              {t("review.negativeTransfer.rule", {
                clicks: current.policy.minimumClicks,
                amount: money.format(evidence.spendThreshold),
              })}
            </span>
            <span>
              {t("review.negativeTransfer.threshold", {
                multiple: current.policy.minimumSpendMultiple,
                reference: money.format(evidence.referenceValue),
                basis: t(`review.negativeTransfer.referenceBasis.${evidence.referenceBasis}`),
                acos: `${((current.policy.targetAcos ?? 0) * 100).toFixed(0)}%`,
              })}
            </span>
            <span>
              {t("review.negativeTransfer.maturity", { days: current.policy.attributionDays })}
            </span>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }
  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-2 gap-4 p-4 md:grid-cols-[0.48fr_0.52fr] md:grid-rows-1 md:px-6">
      <section
        ref={candidateRegion}
        aria-label={t("review.negativeTransfer.candidates")}
        className="flex min-h-0 min-w-0 flex-col gap-2"
      >
        <div className="flex h-8 shrink-0 items-center justify-between gap-2">
          <h3 className="text-xs font-semibold">
            {t("review.negativeTransfer.candidates")}{" "}
            <span className="font-normal tabular-nums text-muted-foreground">{rows.length}</span>
          </h3>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={t("review.negativeTransfer.reload")}
              loading={query.isFetching}
              disabled={!shopId || !enabled}
              onClick={() => void query.refetch()}
            >
              <IconRefresh />
            </Button>
            <ReviewQuickAddTargets
              negative
              targetType="keyword"
              match="exact"
              defaultBid={group.defaultBid.effective}
              bidLimits={capabilities?.bid}
              disabled={disabled}
              onAdd={onAdd}
            />
          </div>
        </div>
        <div className="flex min-w-0 shrink-0 items-center gap-2">
          <Input
            size="sm"
            className="h-8 min-w-0 flex-1 text-xs"
            aria-label={t("review.negativeTransfer.searchCandidates")}
            placeholder={t("review.negativeTransfer.searchCandidates")}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              changeCandidatePage(1);
            }}
          />
          {filter(
            matchFilter,
            (value) => {
              setMatchFilter(value);
              changeCandidatePage(1);
            },
            t("review.negativeTransfer.filterCandidates"),
          )}
        </div>
        <div className="flex min-w-0 shrink-0 items-center gap-2 text-[11px]">
          <Select
            value={sourceFilter}
            onValueChange={(value) => {
              setSourceFilter(value ?? "all");
              changeCandidatePage(1);
            }}
          >
            <SelectTrigger
              size="sm"
              className="h-7 w-31 text-xs"
              aria-label={t("review.negativeTransfer.filterSource")}
            >
              <SelectValue>
                {sourceFilter === "all"
                  ? t("review.negativeTransfer.allSources")
                  : t(`review.negativeTransfer.sources.${sourceFilter}`)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("review.negativeTransfer.allSources")}</SelectItem>
              {["history_negative", "search_term_evidence", "user", "unknown"].map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`review.negativeTransfer.sources.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="truncate text-muted-foreground">
            {t("review.negativeTransfer.defaultUnchecked")}
          </span>
        </div>
        {query.isError ? (
          <ErrorState
            variant="inline"
            message={t("review.negativeTransfer.loadError")}
            onRetry={() => void query.refetch()}
            isRetrying={query.isFetching}
          />
        ) : null}
        <DataTableFrame
          className="flex min-h-0 flex-1 flex-col rounded-lg"
          viewportClassName="min-h-0 flex-1 overflow-auto"
          footer={
            rows.length ? (
              <DataTablePagination
                density="compact"
                aria-label={t("review.negativeTransfer.candidatePagination")}
                page={currentCandidatePage}
                pageCount={candidatePageCount}
                summary={t("review.negativeTransfer.pageSummary", {
                  start: visibleCandidates.length
                    ? (currentCandidatePage - 1) * NEGATIVE_PAGE_SIZE + 1
                    : 0,
                  end: Math.min(
                    currentCandidatePage * NEGATIVE_PAGE_SIZE,
                    visibleCandidates.length,
                  ),
                  total: visibleCandidates.length,
                })}
                onPageChange={changeCandidatePage}
              />
            ) : undefined
          }
        >
          <DataTable
            density="compact"
            stickyHeader
            className="w-full table-fixed text-xs [&_thead_th]:h-8 [&_thead_th]:px-2 [&_thead_th]:text-[10px] [&_thead_th]:normal-case [&_thead_th]:tracking-normal [&_tbody_td]:px-2 [&_tbody_td]:py-1.5 [&_tbody_tr]:h-12"
          >
            <TableHeader>
              <TableRow>
                <TableHead className="w-9">
                  <Checkbox
                    aria-label={t("review.negativeTransfer.selectVisible")}
                    checked={allSelected}
                    indeterminate={partiallySelected}
                    disabled={disabled || !pageCandidates.length}
                    onCheckedChange={(checked) => selectVisible(checked === true)}
                  />
                </TableHead>
                <TableHead>{t("review.parity.target")}</TableHead>
                <TableHead className="w-17">{t("review.parity.match")}</TableHead>
                <TableHead className="w-26">{t("review.parity.source")}</TableHead>
                <TableHead className="w-27">
                  {t("review.negativeTransfer.evidenceColumn")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isPending && query.isFetching
                ? ["first", "second", "third", "fourth", "fifth", "sixth"].map((key) => (
                    <TableRow key={key}>
                      <TableCell colSpan={5}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                : pageCandidates.map((entry) => (
                    <TableRow key={`${identity(entry)}:${entry.candidate?.source ?? "local"}`}>
                      <TableCell>
                        <Checkbox
                          aria-label={t("review.negativeTransfer.selectTarget", {
                            name: entry.label,
                          })}
                          checked={selectedIdentities.has(identity(entry))}
                          disabled={disabled}
                          onCheckedChange={(checked) => select(entry, checked === true)}
                        />
                      </TableCell>
                      <TableCell className="truncate" title={entry.label}>
                        <p className="truncate leading-4">{entry.label}</p>
                        {entry.candidate?.evidence ? (
                          <span className="block truncate text-[10px] text-muted-foreground">
                            {t("review.negativeTransfer.zeroOrders")}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="truncate text-[10px]" title={matchLabel(entry.match)}>
                        {matchLabel(entry.match)}
                      </TableCell>
                      <TableCell className="text-[11px]">
                        {sourceCell(entry, entry.candidate, entry.origins)}
                      </TableCell>
                      <TableCell>
                        {entry.candidate && data ? evidenceCell(entry.candidate, data) : null}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </DataTable>
          {!visibleCandidates.length && !query.isFetching ? (
            <Empty className="min-h-36 gap-2 p-4">
              <EmptyHeader className="gap-2">
                <EmptyMedia className="mb-0">
                  <IconTargetOff className="size-5" />
                </EmptyMedia>
                <EmptyTitle className="text-xs">
                  {t(
                    rows.length
                      ? "review.targetSelection.noResults"
                      : query.isError
                        ? "review.negativeTransfer.unavailable"
                        : "review.negativeTransfer.emptyCandidates",
                  )}
                </EmptyTitle>
                <EmptyDescription className="max-w-80 text-xs">
                  {t(
                    rows.length
                      ? "review.negativeTransfer.changeFilters"
                      : "review.negativeTransfer.manualHint",
                  )}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : null}
        </DataTableFrame>
        {data ? (
          <div
            className="shrink-0 space-y-1 text-[10px] leading-4 text-muted-foreground"
            aria-live="polite"
          >
            <p>
              {t(
                data.history.status === "loaded"
                  ? "review.negativeTransfer.historyLoaded"
                  : "review.negativeTransfer.noHistory",
                { count: data.history.candidates.length },
              )}
              {data.history.freshness !== "fresh"
                ? ` · ${t(`review.negativeTransfer.freshness.${data.history.freshness}`)}`
                : ""}
            </p>
            <p>
              {t(
                data.recommendations.status === "available"
                  ? "review.negativeTransfer.recommendationsAvailable"
                  : data.recommendations.status === "insufficient_evidence"
                    ? "review.negativeTransfer.insufficientEvidence"
                    : "review.negativeTransfer.noRecommendations",
                { count: data.recommendations.candidates.length },
              )}
            </p>
            {data.recommendations.reasons.length ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      type="button"
                      className="inline-flex cursor-default items-center gap-1 text-left"
                    />
                  }
                >
                  <IconInfoCircle className="size-3 shrink-0" />
                  {t(`review.negativeTransfer.reasons.${data.recommendations.reasons[0]}`)}
                </TooltipTrigger>
                <TooltipContent className="smart-creation-preview max-w-96">
                  {data.recommendations.reasons.map((reason) => (
                    <p key={reason}>{t(`review.negativeTransfer.reasons.${reason}`)}</p>
                  ))}
                  <p>
                    {t("review.negativeTransfer.coverage", {
                      days: data.coverage.matureReportDays,
                      total: data.coverage.totalReportDays,
                      groups: data.coverage.attributableAdGroups,
                    })}
                  </p>
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        ) : !shopId ? (
          <p className="text-[11px] text-muted-foreground">
            {t("review.negativeTransfer.unavailable")}
          </p>
        ) : null}
      </section>
      <section
        ref={selectedRegion}
        aria-label={t("review.parity.negativeTargets")}
        className="flex min-h-0 min-w-0 flex-col gap-2"
      >
        <div className="flex h-8 shrink-0 items-center justify-between gap-2">
          <h3 className="text-xs font-semibold" aria-live="polite">
            {t("review.negativeTransfer.selected")}{" "}
            <span className="font-normal tabular-nums text-muted-foreground">{entries.length}</span>
          </h3>
          <Button
            size="xs"
            variant="ghost"
            disabled={disabled || !entries.length}
            onClick={() => onChange([])}
          >
            {t("review.parity.removeAll")}
          </Button>
        </div>
        <div className="flex min-w-0 shrink-0 items-center gap-2">
          <Input
            size="sm"
            className="h-8 min-w-0 flex-1 text-xs"
            aria-label={t("review.negativeTransfer.searchSelected")}
            placeholder={t("review.negativeTransfer.searchSelected")}
            value={selectedSearch}
            onChange={(event) => {
              setSelectedSearch(event.target.value);
              changeSelectedPage(1);
            }}
          />
          {filter(
            selectedMatchFilter,
            (value) => {
              setSelectedMatchFilter(value);
              changeSelectedPage(1);
            },
            t("review.negativeTransfer.filterSelected"),
          )}
        </div>
        <p className="flex h-7 shrink-0 items-center text-[11px] text-muted-foreground">
          {t("review.negativeTransfer.scopeHint", { name: group.name.effective })}
        </p>
        <DataTableFrame
          className="flex min-h-0 flex-1 flex-col rounded-lg"
          viewportClassName="min-h-0 flex-1 overflow-auto"
          footer={
            entries.length ? (
              <DataTablePagination
                density="compact"
                aria-label={t("review.negativeTransfer.selectedPagination")}
                page={currentSelectedPage}
                pageCount={selectedPageCount}
                summary={t("review.negativeTransfer.pageSummary", {
                  start: visibleSelected.length
                    ? (currentSelectedPage - 1) * NEGATIVE_PAGE_SIZE + 1
                    : 0,
                  end: Math.min(currentSelectedPage * NEGATIVE_PAGE_SIZE, visibleSelected.length),
                  total: visibleSelected.length,
                })}
                onPageChange={changeSelectedPage}
              />
            ) : undefined
          }
        >
          <DataTable
            density="compact"
            stickyHeader
            className="w-full table-fixed text-xs [&_thead_th]:h-8 [&_thead_th]:px-2 [&_thead_th]:text-[11px] [&_thead_th]:normal-case [&_thead_th]:tracking-normal [&_tbody_td]:px-2 [&_tbody_td]:py-1.5 [&_tbody_tr]:h-12"
          >
            <TableHeader>
              <TableRow>
                <TableHead>{t("review.parity.target")}</TableHead>
                <TableHead className="w-21">{t("review.parity.match")}</TableHead>
                <TableHead className="w-33">{t("review.parity.source")}</TableHead>
                <TableHead className="w-9" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageSelected.map((entry) => {
                const conflicting = overlappingTargets(entry, positiveEntries);
                return (
                  <TableRow key={entry.id}>
                    <TableCell className="truncate" title={entry.label}>
                      <p className="truncate leading-4">{entry.label}</p>
                      {conflicting.length ? (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-400"
                              />
                            }
                          >
                            <IconAlertTriangle className="size-3 shrink-0" />
                            {t("review.negativeTransfer.overlap")}
                          </TooltipTrigger>
                          <TooltipContent className="smart-creation-preview max-w-96">
                            <p>
                              {t("review.negativeTransfer.overlapTargets", {
                                targets: conflicting.join(", "),
                              })}
                            </p>
                            <p>{t("review.negativeTransfer.overlapDescription")}</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell className="truncate text-[11px]" title={matchLabel(entry.match)}>
                      {matchLabel(entry.match)}
                    </TableCell>
                    <TableCell className="text-[11px]">{sourceCell(entry)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        disabled={disabled}
                        aria-label={t("review.parity.removeTarget", { name: entry.label })}
                        title={t("review.targetTransfer.returnToCandidates")}
                        onClick={() => onChange(entries.filter((item) => item.id !== entry.id))}
                      >
                        <IconX />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </DataTable>
          {!visibleSelected.length ? (
            <Empty className="min-h-36 gap-2 p-4">
              <EmptyHeader className="gap-2">
                <EmptyMedia className="mb-0">
                  <IconTargetOff className="size-5" />
                </EmptyMedia>
                <EmptyTitle className="text-xs">
                  {t(
                    entries.length
                      ? "review.targetSelection.noResults"
                      : "review.sheet.noNegatives",
                  )}
                </EmptyTitle>
                <EmptyDescription className="max-w-72 text-xs">
                  {t(
                    entries.length
                      ? "review.negativeTransfer.changeFilters"
                      : "review.negativeTransfer.emptySelected",
                  )}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : null}
        </DataTableFrame>
        {overlaps.length ? (
          <p
            className="shrink-0 text-[11px] leading-4 text-amber-700 dark:text-amber-400"
            role="status"
          >
            {t("review.negativeTransfer.overlapSummary", { count: overlaps.length })}
          </p>
        ) : (
          <p className="shrink-0 text-[11px] text-muted-foreground">
            {t("review.negativeTransfer.removeHint")}
          </p>
        )}
      </section>
    </div>
  );
}
